import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {IoniconsOutline, WidgetValidator} from '@pt/pt-models';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {WidgetUtils} from '../../widgets/widget.utils';
import {ValidatorGenerator} from '../../utils';

@Component({
  selector: 'pt-input-checkbox',
  templateUrl: './input-checkbox.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputCheckboxComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputCheckboxComponent),
      multi: true
    }
  ]
})
export class InputCheckboxComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() help?: string;
  @Input() error: string;    // Da implementare
  @Input() selectMode: SelectModeConfig;
  @Input() disabled: boolean;

  public formGroup: FormGroup;
  options: InputCheckSelectData[];
  isRequired: boolean;
  inputId: string;
  isChanged = false;
  IconInfo = IoniconsOutline.infoCircle;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    if (this.selectMode) {
      this.options = [
        {
          value: true,
          label: this.selectMode.trueSelectionLabel
        },
        {
          value: false,
          label: this.selectMode.falseSelectionLabel
        }
      ];
    }

    this.inputId = WidgetUtils.generateId('ptWCheckbox_' + this.controlName + '_');

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);

    this.setFormGroup();

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      this.isChanged = t.value !== null;
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });

    if (this.disabled) {
      this.formGroup.controls.value.disable();
    }
  }

  writeValue(obj: any): void {
    this.formGroup.patchValue({value: obj});
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.controls.value.disable();
    } else {
      this.formGroup.controls.value.enable();
    }
  }

  validate(value: FormControl) {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  private setFormGroup() {
    const validator = ValidatorGenerator.generateValidator(this.validators);
    this.formGroup = new FormGroup({
      value: new FormControl(null, validator)
    });
  }
}

export interface InputCheckSelectData {
  label: Observable<string>,
  value: any
}

export interface SelectModeConfig {
  clearable: boolean
  trueSelectionLabel: Observable<string>,
  falseSelectionLabel: Observable<string>,
  placeholder?: Observable<string>
}
